import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adsApi } from '../../utils/api';
import {
    Megaphone,
    Plus,
    Loader2,
    Sparkles,
    DollarSign,
    TrendingUp,
    Eye,
    MousePointer,
    Target,
    Play,
    Pause,
    BarChart3,
    Settings,
    Lightbulb
} from 'lucide-react';

export default function AdsModule() {
    const { projectId } = useParams();
    const queryClient = useQueryClient();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newCampaign, setNewCampaign] = useState({
        name: '',
        platform: 'google',
        budget: '',
        objective: 'traffic',
        targetAudience: ''
    });

    // Fetch campaigns
    const { data: campaignsData, isLoading } = useQuery({
        queryKey: ['ad-campaigns', projectId],
        queryFn: () => adsApi.listCampaigns(projectId!),
        enabled: !!projectId
    });

    // Create campaign mutation
    const createCampaignMutation = useMutation({
        mutationFn: (data: any) => adsApi.createCampaign({ ...data, projectId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ad-campaigns', projectId] });
            setShowCreateModal(false);
            setNewCampaign({ name: '', platform: 'google', budget: '', objective: 'traffic', targetAudience: '' });
        }
    });

    // Activate/Pause campaign mutation
    const activateMutation = useMutation({
        mutationFn: (id: string) => adsApi.activateCampaign(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ad-campaigns', projectId] })
    });

    const pauseMutation = useMutation({
        mutationFn: (id: string) => adsApi.pauseCampaign(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ad-campaigns', projectId] })
    });

    // Get optimization suggestions
    const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
    const { data: suggestionsData, isLoading: suggestionsLoading } = useQuery({
        queryKey: ['ad-suggestions', selectedCampaignId],
        queryFn: () => adsApi.getOptimizationSuggestions(selectedCampaignId!),
        enabled: !!selectedCampaignId
    });

    const campaigns = campaignsData?.data?.campaigns || [];

    const getPlatformColor = (platform: string) => {
        switch (platform) {
            case 'google': return 'text-blue-400 bg-blue-500/20';
            case 'facebook': return 'text-blue-500 bg-blue-600/20';
            case 'instagram': return 'text-pink-400 bg-pink-500/20';
            case 'linkedin': return 'text-sky-400 bg-sky-500/20';
            default: return 'text-primary-400 bg-primary-500/20';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-500/20 text-green-400';
            case 'paused': return 'bg-yellow-500/20 text-yellow-400';
            case 'completed': return 'bg-dark-700 text-dark-400';
            default: return 'bg-dark-700 text-dark-400';
        }
    };

    // Calculate totals
    const totalSpend = campaigns.reduce((sum: number, c: any) => sum + (c.spent || 0), 0);
    const totalImpressions = campaigns.reduce((sum: number, c: any) => sum + (c.impressions || 0), 0);
    const totalClicks = campaigns.reduce((sum: number, c: any) => sum + (c.clicks || 0), 0);
    const avgCTR = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Megaphone className="w-8 h-8 text-orange-400" />
                        Ads Manager
                    </h1>
                    <p className="text-dark-400 mt-1">Create and optimize ad campaigns with AI assistance</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="btn btn-primary flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    New Campaign
                </button>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-4 gap-4">
                {[
                    { label: 'Total Spend', value: `$${totalSpend.toLocaleString()}`, icon: DollarSign, color: 'text-green-400' },
                    { label: 'Impressions', value: totalImpressions.toLocaleString(), icon: Eye, color: 'text-blue-400' },
                    { label: 'Clicks', value: totalClicks.toLocaleString(), icon: MousePointer, color: 'text-purple-400' },
                    { label: 'Avg CTR', value: `${avgCTR}%`, icon: TrendingUp, color: 'text-orange-400' }
                ].map((stat) => (
                    <div key={stat.label} className="card p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-dark-400 text-sm">{stat.label}</p>
                                <p className="text-2xl font-bold mt-1">{stat.value}</p>
                            </div>
                            <stat.icon className={`w-8 h-8 ${stat.color} opacity-50`} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Create Campaign Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-dark-900 rounded-2xl p-6 w-full max-w-lg border border-dark-800">
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                            <Megaphone className="w-5 h-5 text-orange-400" />
                            Create Campaign
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="label">Campaign Name</label>
                                <input
                                    type="text"
                                    value={newCampaign.name}
                                    onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                                    className="input"
                                    placeholder="Summer Sale Campaign"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">Platform</label>
                                    <select
                                        value={newCampaign.platform}
                                        onChange={(e) => setNewCampaign({ ...newCampaign, platform: e.target.value })}
                                        className="input"
                                    >
                                        <option value="google">Google Ads</option>
                                        <option value="facebook">Facebook Ads</option>
                                        <option value="instagram">Instagram Ads</option>
                                        <option value="linkedin">LinkedIn Ads</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="label">Daily Budget ($)</label>
                                    <input
                                        type="number"
                                        value={newCampaign.budget}
                                        onChange={(e) => setNewCampaign({ ...newCampaign, budget: e.target.value })}
                                        className="input"
                                        placeholder="50"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="label">Objective</label>
                                <select
                                    value={newCampaign.objective}
                                    onChange={(e) => setNewCampaign({ ...newCampaign, objective: e.target.value })}
                                    className="input"
                                >
                                    <option value="traffic">Website Traffic</option>
                                    <option value="conversions">Conversions</option>
                                    <option value="awareness">Brand Awareness</option>
                                    <option value="leads">Lead Generation</option>
                                </select>
                            </div>

                            <div>
                                <label className="label">Target Audience</label>
                                <textarea
                                    value={newCampaign.targetAudience}
                                    onChange={(e) => setNewCampaign({ ...newCampaign, targetAudience: e.target.value })}
                                    className="input h-20 resize-none"
                                    placeholder="Describe your target audience..."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                                Cancel
                            </button>
                            <button
                                onClick={() => createCampaignMutation.mutate(newCampaign)}
                                disabled={!newCampaign.name || createCampaignMutation.isPending}
                                className="btn btn-primary flex items-center gap-2"
                            >
                                {createCampaignMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Plus className="w-4 h-4" />
                                )}
                                Create Campaign
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Suggestions Panel */}
            {selectedCampaignId && (
                <div className="card p-6 border-primary-500/30">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                            <Lightbulb className="w-5 h-5 text-yellow-400" />
                            AI Optimization Suggestions
                        </h3>
                        <button onClick={() => setSelectedCampaignId(null)} className="text-dark-400 hover:text-white">
                            ✕
                        </button>
                    </div>
                    {suggestionsLoading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-primary-400" />
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {(suggestionsData?.data?.suggestions || []).map((suggestion: any, i: number) => (
                                <div key={i} className="p-3 bg-dark-800/50 rounded-lg">
                                    <p className="text-sm">{suggestion.text || suggestion}</p>
                                </div>
                            ))}
                            {(!suggestionsData?.data?.suggestions || suggestionsData.data.suggestions.length === 0) && (
                                <p className="text-dark-400 text-center py-4">No suggestions available yet. Run the campaign to get AI insights.</p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Campaigns List */}
            {isLoading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
                </div>
            ) : campaigns.length === 0 ? (
                <div className="card p-12 text-center">
                    <Megaphone className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No campaigns yet</h3>
                    <p className="text-dark-400 mb-4">Create your first ad campaign to start driving traffic</p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="btn btn-primary inline-flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Create Campaign
                    </button>
                </div>
            ) : (
                <div className="grid gap-4">
                    {campaigns.map((campaign: any) => (
                        <div key={campaign.id} className="card p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className={`p-3 rounded-xl ${getPlatformColor(campaign.platform)}`}>
                                        <Megaphone className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold">{campaign.name}</h3>
                                            <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(campaign.status)}`}>
                                                {campaign.status}
                                            </span>
                                        </div>
                                        <p className="text-dark-400 text-sm capitalize">{campaign.platform} • {campaign.objective}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-8">
                                    <div className="text-right">
                                        <p className="text-dark-400 text-xs">Budget</p>
                                        <p className="font-semibold">${campaign.budget || 0}/day</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-dark-400 text-xs">Spent</p>
                                        <p className="font-semibold">${campaign.spent || 0}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-dark-400 text-xs">Clicks</p>
                                        <p className="font-semibold">{campaign.clicks || 0}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setSelectedCampaignId(campaign.id)}
                                            className="p-2 text-dark-400 hover:text-primary-400 transition-colors"
                                            title="AI Suggestions"
                                        >
                                            <Sparkles className="w-5 h-5" />
                                        </button>
                                        {campaign.status === 'active' ? (
                                            <button
                                                onClick={() => pauseMutation.mutate(campaign.id)}
                                                className="p-2 text-dark-400 hover:text-yellow-400 transition-colors"
                                            >
                                                <Pause className="w-5 h-5" />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => activateMutation.mutate(campaign.id)}
                                                className="p-2 text-dark-400 hover:text-green-400 transition-colors"
                                            >
                                                <Play className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
